import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Download,
  Eye,
  FileText,
  Loader2,
  X,
  User,
} from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";

const PaymentsSection = ({ candidate }) => {
  const [payslips, setPayslips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [pdfModal, setPdfModal] = useState({ open: false, url: null, title: "" });

  const loadPayslips = useCallback(async () => {
    if (!candidate?.pld) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const res = await axios.get(`payslip/candidate/${candidate.pld}`);
      setPayslips(res.data);
    } catch (error) {
      console.error("Erreur lors du chargement des bulletins:", error);
    } finally {
      setIsLoading(false);
    }
  }, [candidate?.pld]);

  useEffect(() => {
    loadPayslips();
  }, [loadPayslips]);

  const handleView = async (payslip) => {
    setActionLoading({ id: payslip._id, type: "view" });
    try {
      const res = await axios.get(`payslip/${payslip._id}`);
      const base64 = res.data.data;
      if (!base64) {
        toast.error("Aucun PDF disponible pour ce bulletin");
        return;
      }
      const blob = base64ToBlob(base64, "application/pdf");
      const url = URL.createObjectURL(blob);
      setPdfModal({
        open: true,
        url,
        title: `Bulletin de paie - ${formatDate(payslip.date)}`,
      });
    } catch (error) {
      console.error("Erreur lors de la visualisation:", error);
      toast.error("Impossible de charger le bulletin");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async (payslip) => {
    setActionLoading({ id: payslip._id, type: "download" });
    try {
      const res = await axios.get(`payslip/${payslip._id}`);
      const base64 = res.data.data;
      if (!base64) {
        toast.error("Aucun PDF disponible pour ce bulletin");
        return;
      }
      const blob = base64ToBlob(base64, "application/pdf");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `bulletin_${payslip.candidateName}_${payslip.date || "paie"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Bulletin téléchargé");
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      toast.error("Impossible de télécharger le bulletin");
    } finally {
      setActionLoading(null);
    }
  };

  const base64ToBlob = (base64, type) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type });
  };

  const closePdfModal = () => {
    if (pdfModal.url) URL.revokeObjectURL(pdfModal.url);
    setPdfModal({ open: false, url: null, title: "" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date non définie";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
    });
  };

  const groupByYear = (items) => {
    const groups = {};
    items.forEach((item) => {
      const date = new Date(item.date);
      const year = isNaN(date.getTime()) ? "Autre" : date.getFullYear().toString();
      if (!groups[year]) groups[year] = [];
      groups[year].push(item);
    });
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
      </div>
    );
  }

  const grouped = groupByYear(payslips);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bulletins de paie</h2>
          <p className="text-gray-600 mt-1">
            {payslips.length} bulletin{payslips.length !== 1 ? "s" : ""} disponible{payslips.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
          <FileText className="w-5 h-5 text-blue-600" />
          <span className="text-2xl font-bold text-blue-900">{payslips.length}</span>
        </div>
      </div>

      {/* Liste */}
      {payslips.length === 0 ? (
        <motion.div
          className="text-center py-12 bg-gray-50 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun bulletin de paie
          </h3>
          <p className="text-gray-600">
            Les bulletins apparaîtront ici une fois disponibles
          </p>
        </motion.div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([year, items]) => (
            <div key={year}>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                {year}
                <span className="text-sm font-normal text-gray-500">
                  ({items.length} bulletin{items.length !== 1 ? "s" : ""})
                </span>
              </h3>
              <div className="grid gap-3">
                <AnimatePresence>
                  {items.map((payslip, index) => (
                    <motion.div
                      key={payslip._id}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center justify-between hover:shadow-md transition-shadow"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 rounded-lg">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {formatDate(payslip.date)}
                          </p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {payslip.candidateName}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <motion.button
                          onClick={() => handleView(payslip)}
                          disabled={actionLoading?.id === payslip._id && actionLoading?.type === "view"}
                          className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {actionLoading?.id === payslip._id && actionLoading?.type === "view" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                          <span className="text-sm font-medium hidden sm:inline">Voir</span>
                        </motion.button>
                        <motion.button
                          onClick={() => handleDownload(payslip)}
                          disabled={actionLoading?.id === payslip._id && actionLoading?.type === "download"}
                          className="flex items-center gap-2 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {actionLoading?.id === payslip._id && actionLoading?.type === "download" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          <span className="text-sm font-medium hidden sm:inline">Télécharger</span>
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal PDF */}
      <AnimatePresence>
        {pdfModal.open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePdfModal}
          >
            <motion.div
              className="bg-white w-[90vw] max-w-[1100px] h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  {pdfModal.title}
                </h2>
                <button
                  onClick={closePdfModal}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 p-2">
                <iframe
                  src={pdfModal.url}
                  className="w-full h-full rounded-lg"
                  title={pdfModal.title}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentsSection;
